import { useEffect, useState } from 'react';
import {
  pageFrameSize,
  pageRectToRasterPixels,
} from '@shared/helpers/project-file-drawing-crop-rect.helpers';
import type {
  ExtractedDrawing,
  ProjectFilePageRaster,
} from '@shared/models/project-file-drawing.model';
import { adminProjectFileBatchesApi } from './admin-project-file-batches.api';

/** Long edge of the on-screen crop. The stored page raster stays full size. */
const PREVIEW_LONG_EDGE = 2048;

type PageImage = {
  image: HTMLImageElement;
  url: string;
};

const pageImages = new Map<string, Promise<PageImage>>();

const pageImageKey = (
  batchId: string,
  fileId: string,
  raster: ProjectFilePageRaster,
): string => `${batchId}/${fileId}/${raster.pageIndex}/${raster.blobRef}`;

const loadPageImage = (
  batchId: string,
  fileId: string,
  raster: ProjectFilePageRaster,
): Promise<PageImage> => {
  const key = pageImageKey(batchId, fileId, raster);
  const existing = pageImages.get(key);
  if (existing) return existing;
  const pending = adminProjectFileBatchesApi
    .getPageRaster(batchId, fileId, raster.pageIndex)
    .then(
      (blob) =>
        new Promise<PageImage>((resolve, reject) => {
          const url = URL.createObjectURL(blob);
          const image = new Image();
          image.onload = () => resolve({ image, url });
          image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('page raster failed'));
          };
          image.src = url;
        }),
    );
  pageImages.set(key, pending);
  pending.catch(() => {
    pageImages.delete(key);
  });
  return pending;
};

const cropObjectUrl = (
  image: CanvasImageSource,
  pixels: { x: number; y: number; width: number; height: number },
): Promise<string> =>
  new Promise((resolve, reject) => {
    const long = Math.max(pixels.width, pixels.height);
    const scale = long > PREVIEW_LONG_EDGE ? PREVIEW_LONG_EDGE / long : 1;
    const width = Math.max(1, Math.round(pixels.width * scale));
    const height = Math.max(1, Math.round(pixels.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('canvas'));
      return;
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(
      image,
      pixels.x,
      pixels.y,
      pixels.width,
      pixels.height,
      0,
      0,
      width,
      height,
    );
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('crop'));
        return;
      }
      resolve(URL.createObjectURL(blob));
    }, 'image/png');
  });

/**
 * Object URL for one drawing.
 * A page raster plus {@link ExtractedDrawing.rectPt} is drawn with
 * `drawImage`. A legacy row still loads its own image.
 */
export const useDrawingCropUrl = (
  batchId: string,
  fileId: string,
  drawing: ExtractedDrawing,
  pageRaster: ProjectFilePageRaster | null,
): string | null => {
  const [url, setUrl] = useState<string | null>(null);
  const rect = drawing.rectPt;
  const onPage =
    pageRaster != null &&
    rect != null &&
    pageRaster.pageIndex === drawing.pageIndex;

  useEffect(() => {
    let cropUrl: string | undefined;
    let cancelled = false;
    const apply = (next: string | null) => {
      if (cancelled) {
        if (next) URL.revokeObjectURL(next);
        return;
      }
      cropUrl = next ?? undefined;
      setUrl(next);
    };

    if (!onPage || pageRaster == null || rect == null) {
      if (drawing.blobRef == null) {
        apply(null);
      } else {
        void adminProjectFileBatchesApi
          .getDrawing(batchId, fileId, drawing.id)
          .then((blob) => {
            apply(URL.createObjectURL(blob));
          })
          .catch(() => apply(null));
      }
    } else {
      const pixels = pageRectToRasterPixels(
        rect,
        pageFrameSize(pageRaster.pageFrame),
        pageRaster,
      );
      if (pixels == null) {
        apply(null);
      } else {
        void loadPageImage(batchId, fileId, pageRaster)
          .then((page) => cropObjectUrl(page.image, pixels))
          .then((next) => apply(next))
          .catch(() => apply(null));
      }
    }

    return () => {
      cancelled = true;
      if (cropUrl) URL.revokeObjectURL(cropUrl);
    };
  }, [
    batchId,
    fileId,
    drawing.id,
    drawing.blobRef,
    drawing.pageIndex,
    onPage,
    pageRaster,
    rect,
  ]);

  return url;
};
